import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { Check, X } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const STAGES = [
  { k: "Person" }, { k: "Circumstances" }, { k: "Opportunities" }, { k: "Research" },
  { k: "Challenge" }, { k: "Evidence" }, { k: "Verdict" }, { k: "Next action" },
];

// Representative payload shown inside the "engine viewport" for the active stage. Illustrative only — no backend.
function Payload({ i }) {
  const common = "absolute inset-0 flex flex-col justify-center gap-2";
  if (i === 0) return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">Profile enters</p>
      <p className="font-heading font-bold text-xl text-donkey-warm">Sam, 42 — ex-accountant</p>
      <p className="text-sm text-donkey-grey">“I've got £500 and can work evenings.”</p>
    </div>
  );
  if (i === 1) return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">Understood as signals</p>
      <div className="flex flex-wrap gap-2">
        {["BUDGET £500 ✓", "EVENINGS ✓", "REMOTE ✓", "XERO PRO ✓"].map((c, j) => (
          <motion.span key={c} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: j * 0.12 }}
            className="text-[11px] rounded-full border border-donkey-orange/30 bg-donkey-orange/10 text-donkey-warmorange px-2.5 py-1">{c}</motion.span>
        ))}
      </div>
    </div>
  );
  if (i === 2) return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">Three opportunities generated</p>
      <div className="grid grid-cols-3 gap-2">
        {["Bookkeeping for trades", "Finance templates", "Cashflow coaching"].map((c, j) => (
          <motion.div key={c} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: j * 0.14 }}
            className="rounded-lg border border-white/12 bg-donkey-bg px-2.5 py-2 text-xs text-donkey-warm/90">{c}</motion.div>
        ))}
      </div>
    </div>
  );
  if (i === 3) return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">Live market research</p>
      {["Demand", "Competition", "Reachability"].map((c, j) => (
        <div key={c} className="flex items-center gap-2">
          <span className="text-[11px] text-donkey-grey w-24 shrink-0">{c}</span>
          <div className="flex-1 h-1.5 rounded-full bg-white/8 overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${62 + j * 10}%` }} transition={{ delay: 0.15 + j * 0.15, duration: 0.7 }} className="h-full bg-gradient-to-r from-donkey-orange to-donkey-amber" />
          </div>
        </div>
      ))}
    </div>
  );
  if (i === 4) return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">Assumptions challenged</p>
      <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}
        className="inline-flex items-center justify-between gap-2 rounded-lg border border-donkey-red/40 bg-donkey-red/5 px-3 py-2 text-sm text-donkey-grey line-through decoration-donkey-red/70">
        “Everyone needs a bookkeeper” <X className="w-4 h-4 text-donkey-red no-underline" />
      </motion.div>
      <p className="text-xs text-donkey-amber">Rejected — too vague to build on.</p>
    </div>
  );
  if (i === 5) return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">Evidence collected</p>
      {["Tradespeople pay for this", "Reachable without paid ads"].map((c, j) => (
        <motion.div key={c} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: j * 0.15 }}
          className="inline-flex items-center gap-2 rounded-lg border border-donkey-green/40 bg-donkey-green/10 px-3 py-2 text-sm text-donkey-warm">
          <Check className="w-4 h-4 text-donkey-green" />{c}</motion.div>
      ))}
    </div>
  );
  if (i === 6) return (
    <div className={`${common} items-start`}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">The verdict lands</p>
      <div className="flex items-center gap-4">
        <div className="relative w-16 h-16 rounded-full border-2 border-donkey-green flex items-center justify-center">
          <span className="font-mono text-donkey-green text-lg">74</span>
        </div>
        <div>
          <span className="inline-block text-xs rounded-full border border-donkey-green/40 bg-donkey-green/10 text-donkey-green px-2.5 py-1 mb-1">WORTH TESTING</span>
          <p className="text-sm text-donkey-warm">Real demand and you can reach them cheaply.</p>
        </div>
      </div>
    </div>
  );
  return (
    <div className={common}>
      <p className="text-[11px] uppercase tracking-wider text-donkey-grey">One clear next action</p>
      <p className="font-heading font-bold text-lg text-donkey-warm">Run a 1-week demand test with 3 local firms.</p>
      <p className="text-sm text-donkey-grey">Evidence before you spend serious money.</p>
    </div>
  );
}

// SIGNATURE: a look inside the Donkey's intelligence engine — information visibly travels through the pipeline.
export function IntelligencePipeline() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3 });
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduce || !inView) return;
    const id = setInterval(() => setI((p) => (p + 1) % STAGES.length), 2000);
    return () => clearInterval(id);
  }, [inView, reduce]);
  const pct = (i / (STAGES.length - 1)) * 100;

  return (
    <section ref={ref} className="relative rounded-3xl border border-white/10 bg-donkey-bg/50 overflow-hidden p-6 sm:p-8 mb-12" data-testid="intelligence-pipeline">
      <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-donkey-orange/12 blur-[100px] pointer-events-none" aria-hidden />
      <p className="relative text-xs uppercase tracking-[0.2em] text-donkey-orange mb-1">Inside the engine</p>
      <h2 className="relative font-heading font-bold text-2xl sm:text-3xl text-donkey-warm">Watch the Donkey process someone</h2>
      <p className="relative text-sm text-donkey-grey mt-2 max-w-2xl">An illustration of how raw information becomes a researched, challenged, evidence-based verdict.</p>

      {/* pipeline track */}
      <div className="relative mt-8">
        <div className="absolute left-0 right-0 top-[11px] h-px bg-white/10 hidden sm:block" aria-hidden />
        <motion.div className="absolute left-0 top-[11px] h-px bg-gradient-to-r from-donkey-orange to-donkey-warmorange hidden sm:block" style={{ boxShadow: "0 0 10px rgba(249,115,22,0.6)" }}
          initial={false} animate={{ width: `${pct}%` }} transition={{ duration: 0.5, ease: EASE }} aria-hidden />
        <div className="relative flex flex-wrap sm:flex-nowrap gap-y-3 sm:justify-between">
          {STAGES.map((s, j) => {
            const done = j <= i;
            return (
              <div key={s.k} className="flex sm:flex-col items-center gap-2 sm:gap-1.5 sm:flex-1 min-w-0">
                <motion.span initial={false} animate={{ scale: j === i ? 1.15 : 1 }} transition={{ duration: 0.3 }}
                  className={`w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center shrink-0 transition-colors duration-400
                    ${done ? "border-donkey-orange bg-donkey-orange/20" : "border-white/20 bg-donkey-black"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${done ? "bg-donkey-orange" : "bg-white/25"}`} />
                </motion.span>
                <span className={`text-[10px] uppercase tracking-wider sm:text-center transition-colors ${j === i ? "text-donkey-warmorange" : "text-donkey-grey"}`}>{s.k}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* engine viewport — payload morphs as data travels */}
      <div className="relative mt-7 rounded-2xl border border-white/10 bg-donkey-black/60 p-5 h-[9.5rem] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={reduce ? false : { opacity: 0, x: 24, filter: "blur(4px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={reduce ? undefined : { opacity: 0, x: -24, filter: "blur(3px)" }}
            transition={{ duration: 0.45, ease: EASE }}
            className="absolute inset-5"
          >
            <Payload i={i} />
          </motion.div>
        </AnimatePresence>
      </div>
      <p className="relative text-[11px] text-donkey-grey/80 mt-3">Illustrative demonstration — the Donkey doesn't analyse a real person here. Try a full example below.</p>
    </section>
  );
}

export default IntelligencePipeline;
